import { useEffect, useMemo, useState } from "react";
import {
    Container,
    Typography,
    Grid,
    Card,
    CardContent,
    Box,
    Chip,
    useTheme,
} from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import GavelIcon from "@mui/icons-material/Gavel";
import DescriptionIcon from "@mui/icons-material/Description";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useGetClientDetailsQuery } from "src/reduxServer/services/client";
import Loader from "../../../components/Loader";
import NoDataFound from "../../../components/NoDataFound";
import PageTypography from "src/components/PageTypography/PageTypography";
// import AddConsultationDialog from "./addConsultations";
import PageTitleWrapper from "src/components/PageTitleWrapper";
import PageHeader from "src/components/pageHeader";
import { getConsultationStatusColor, getSingleCaseStatusColor } from "src/utils/help";
import NewNotificationDialog from "../notification/component/notificationAdd";
import { Helmet } from "react-helmet-async";

const SectionCard = ({
    icon,
    title,
    children,
    action
}: {
    icon: React.ReactNode;
    title: string;
    children: React.ReactNode;
    action?: React.ReactNode;
}) => {
    const theme = useTheme();


    return (


        <>
            <Helmet>
                <title>Clients Details</title>
            </Helmet>


            <Card sx={{ boxShadow: 3, borderRadius: 2 }}>
                <CardContent>
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            mb: 3,
                            pb: 2,
                            borderBottom: `1px solid ${theme.palette.primary.light}`
                        }}
                    >
                        {/* Left: Icon + Title */}
                        <Box display="flex" alignItems="center">
                            {icon}
                            <PageTypography variant="h5" fontWeight={700}>
                                {title}
                            </PageTypography>
                        </Box>

                        {/* Right: Action (Edit icon) */}
                        {action}
                    </Box>
                    {children}
                </CardContent>
            </Card>
        </>
    );
};

const DataRow = ({
    label,
    value
}: {
    label: string;
    value?: React.ReactNode;
}) => (
    <Box
        sx={{
            display: "flex",
            alignItems: "center",
            mb: 1.5,
            columnGap: 1
        }}
    >
        <PageTypography fontWeight={600} component="span">
            {label}:
        </PageTypography>

        <PageTypography color="text.secondary" component="span">
            {value || "-"}
        </PageTypography>
    </Box>
);




const ClientDetails = () => {
    const { t } = useTranslation();
    const theme = useTheme();
    const { id } = useParams();
    const [open, setOpen] = useState(false)

    const { data, isLoading } = useGetClientDetailsQuery(id!, {
        skip: !id,
    });

    console.log("client Data>>>>>", data);


    // ✅ Correct data extraction
    const client = data?.data;

    // 🔹 LOADING
    if (isLoading) {
        return <Loader />;
    }

    // 🔹 NO DATA
    if (!client) {
        return <NoDataFound />;
    }

    return (
        <>
            <PageTitleWrapper>
                <PageHeader title={t("client_details")}

                    buttonLabel={t("Notifications")}
                    onButtonClick={() => setOpen(true)}
                />
            </PageTitleWrapper>

            <Container maxWidth="xl" sx={{ mt: 6 }}>
                <Grid container spacing={4}>

                    {/* ================= CLIENT INFORMATION ================= */}
                    <Grid item xs={12} >
                        <SectionCard
                            title={t("Client Information")}
                            icon={<PersonIcon sx={{ fontSize: 30 }} />}
                        >
                            <DataRow
                                label={t("Name")}
                                value={`${client.first_name} ${client.last_name}`}
                            />
                            <DataRow label={t("Email")} value={client.email} />
                            <DataRow label={t("Mobile")} value={client.mobile} />
                            <DataRow
                                label={t("Gender")}
                                value={client.gender === "M" ? t("Male") : t("Female")}
                            />
                            <DataRow
                                label={t("Status")}
                                value={
                                    <Chip
                                        label={client.is_active ? t("Active") : t("Inactive")}
                                        color={client.is_active ? "success" : "error"}
                                        size="small"
                                    />
                                }
                            />
                        </SectionCard>
                    </Grid>

                    {/* ================= CASES ================= */}
                    <Grid item xs={12} md={6}>
                        <SectionCard
                            title={t("Cases")}
                            icon={<GavelIcon sx={{ fontSize: 30 }} />}
                        >
                            {!client.cases?.length ? (
                                <Typography color="text.secondary">
                                    {t("no_case")}
                                </Typography>
                            ) : (
                                client.cases.map((item) => (
                                    <Box
                                        key={item.id}
                                        mb={2}
                                        p={2}
                                        border="1px solid"
                                        borderColor="divider"
                                        borderRadius={2}
                                    >
                                        <DataRow label={t("Title")} value={item.title} />
                                        <DataRow
                                            label={t("Status")}
                                            value={
                                                <Chip
                                                    label={t(item.status)}
                                                    size="small"
                                                    color="primary"
                                                    sx={{
                                                        bgcolor: getSingleCaseStatusColor(item.status),
                                                        color: "#fff",
                                                        fontWeight: 500,
                                                        textTransform: "capitalize",
                                                    }}
                                                />
                                            }
                                        />
                                        <DataRow
                                            label={t("Lawyer")}
                                            value={`${item.lawyer?.first_name} ${item.lawyer?.last_name}`}
                                        />
                                    </Box>
                                ))
                            )}
                        </SectionCard>
                    </Grid>

                    {/* ================= CONSULTATIONS ================= */}
                    <Grid item xs={12} md={6}>
                        <SectionCard
                            title={t("Consultations")}
                            icon={<DescriptionIcon sx={{ fontSize: 30 }} />}
                        >
                            {!client.consultations?.length ? (
                                <Typography color="text.secondary">
                                    {t("No Consultations Found")}
                                </Typography>
                            ) : (
                                client.consultations.map((item) => (
                                    <Box
                                        key={item.id}
                                        mb={2}
                                        p={2}
                                        border="1px solid"
                                        borderColor="divider"
                                        borderRadius={2}
                                    >
                                        <DataRow label={t("Title")} value={item.title} />
                                        <DataRow
                                            label={t("Status")}
                                            value={
                                                <Chip
                                                    label={t(item.status)}
                                                    size="small"
                                                    color="secondary"
                                                    sx={{
                                                        bgcolor: getConsultationStatusColor(item.status),
                                                        color: "#fff",
                                                        fontWeight: 500,
                                                        textTransform: "capitalize",
                                                    }}
                                                />
                                            }
                                        />
                                        <DataRow
                                            label="Lawyer"
                                            value={
                                                item.lawyer ? (
                                                    `${item.lawyer.first_name} ${item.lawyer.last_name}`
                                                ) : (
                                                    <Chip label="Not Assigned" size="small" color="warning" />
                                                )
                                            }
                                        />
                                    </Box>
                                ))
                            )}
                        </SectionCard>
                    </Grid>

                </Grid>
            </Container>
            <NewNotificationDialog
                openDialog={open} setOpenDialog={() => setOpen(false)} />
        </>
    );
};


export default ClientDetails;
